import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon, PencilIcon, LanguageIcon, CheckIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { shopifyService, ShopifyProduct } from '../services/shopifyService';
import { CategoryCombobox } from './CategoryCombobox';
import AutoGrowTextarea from './AutoGrowTextarea';
import KeywordSelector from './KeywordSelector';
import toast from 'react-hot-toast';

// Language display names (German is the primary/original language)
const LANGUAGE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  nl: 'Nederlands'
};

// Import the RefinedProductData type structure for the refine callback
interface ProductMetadata {
  name: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  materials: string[];
  dimensions: {
    height: number;
    width: number;
    depth: number;
    weight: number;
  };
  tags: string[];
}

interface MultiLanguageMetadata {
  de: ProductMetadata;
  en: ProductMetadata;
  fr: ProductMetadata;
  es: ProductMetadata;
}

interface RefinedProductData {
  refinedMetadata: MultiLanguageMetadata;
  sharedInformation: Record<string, unknown>;
  originalData: Record<string, unknown>;
  correlationId: string;
  agentSummary: string;
  shopify_product_id?: string;
  images?: string[];
}

interface ProductDetailsProps {
  /**
   * Callback when user wants to refine/edit the product
   */
  onRefineProduct?: (product: RefinedProductData) => void;
}

// Editable field component
interface EditableFieldProps {
  label: string;
  value: string | number | null | undefined;
  isEditing: boolean;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'textarea';
  suffix?: string;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({ 
  label, value, isEditing, onChange, type = 'text', suffix, className = ''
}) => {
  if (isEditing) {
    if (type === 'textarea') {
      return (
        <div className={className}>
          <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
          <AutoGrowTextarea
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2"
            minRows={2}
            maxRows={20}
          />
        </div>
      );
    }
    return (
      <div className={className}>
        <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
        <div className="relative">
          <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2"
          />
          {suffix && (
            <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">{suffix}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value ?? '-'}{suffix && value ? ` ${suffix}` : ''}
      </dd>
    </div>
  );
};

// Section names for per-section editing
type EditableSection = 'info' | 'dimensions' | 'ceramics' | 'description' | 'pricing' | 'tags' | 'images' | null;

// Section header component with inline edit button
interface SectionHeaderProps {
  title: string;
  section: EditableSection;
  editingSection: EditableSection;
  onEdit: (section: EditableSection) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ 
  title, section, editingSection, onEdit, onSave, onCancel, isSaving 
}) => {
  const isThisSectionEditing = editingSection === section;
  
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      {isThisSectionEditing ? (
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Abbrechen
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                Speichern...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Speichern
              </>
            )}
          </button>
        </div>
      ) : (
        <button
          onClick={() => onEdit(section)}
          disabled={editingSection !== null}
          className="inline-flex items-center px-3 py-1 border border-clay-300 text-sm font-medium rounded-md text-clay-700 bg-white hover:bg-clay-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PencilIcon className="h-4 w-4 mr-1" />
          Bearbeiten
        </button>
      )}
    </div>
  );
};

const ProductDetails: React.FC<ProductDetailsProps> = ({ onRefineProduct }) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('de');
  const [existingKeywords, setExistingKeywords] = useState<string[]>([]);  // Keywords from existing products
  
  // AI regeneration state for German content
  const [isRegeneratingGerman, setIsRegeneratingGerman] = useState(false);
  
  // Per-section edit mode state
  const [editingSection, setEditingSection] = useState<EditableSection>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editedProduct, setEditedProduct] = useState<ShopifyProduct | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Helper to check if any section is being edited
  const isEditing = editingSection !== null;
  
  // Translation editing state
  const [isEditingTranslation, setIsEditingTranslation] = useState(false);
  const [editedTranslation, setEditedTranslation] = useState<{ title: string; body_html: string }>({ title: '', body_html: '' });
  const [isSavingTranslation, setIsSavingTranslation] = useState(false);
  
  // Translation generation state
  const [isGeneratingTranslations, setIsGeneratingTranslations] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Produkt-ID fehlt');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Use the new method that fetches translations
        const foundProduct = await shopifyService.getProductWithTranslations(productId);
        if (foundProduct) {
          setProduct(foundProduct);
          setEditedProduct(JSON.parse(JSON.stringify(foundProduct))); // Deep copy for editing
        } else {
          setError('Produkt nicht gefunden');
        }
      } catch (err) {
        console.error('Failed to fetch product:', err);
        setError('Fehler beim Laden des Produkts');
        toast.error('Produkt konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Fetch existing keywords from products for suggestions
  useEffect(() => {
    const fetchExistingKeywords = async () => {
      try {
        const response = await shopifyService.getProducts(100);
        if (response.success && response.products) {
          const allTags = new Set<string>();
          response.products.forEach((p: ShopifyProduct) => {
            const tags = p.tags;
            if (tags) {
              const tagList = typeof tags === 'string' 
                ? tags.split(',').map(t => t.trim().toLowerCase()) 
                : tags.map(t => t.toLowerCase());
              tagList.filter(Boolean).forEach(tag => allTags.add(tag));
            }
          });
          setExistingKeywords(Array.from(allTags).sort());
        }
      } catch (error) {
        console.error('Failed to fetch existing keywords:', error);
      }
    };

    fetchExistingKeywords();
  }, []);

  // Handle unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: globalThis.BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // The browser will show a confirmation dialog
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const formatPrice = (price: string | number): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(numPrice);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get available languages from translations (German 'de' is always the primary/original)
  const getAvailableLanguages = (): string[] => {
    const languages = ['de']; // German is always the primary language
    const p = isEditing ? editedProduct : product;
    if (p?.translations) {
      Object.keys(p.translations).forEach(locale => {
        // Skip 'de' since it's already included, and add other languages
        if (locale !== 'de' && !languages.includes(locale)) {
          languages.push(locale);
        }
      });
    }
    return languages;
  };

  // Get title for selected language (German 'de' uses the original product title)
  const getTranslatedTitle = (): string => {
    const p = isEditing ? editedProduct : product;
    if (selectedLanguage === 'de' || !p?.translations?.[selectedLanguage]?.title) {
      return p?.title || '';
    }
    return p.translations[selectedLanguage].title || p.title;
  };

  // Get description for selected language (German 'de' uses the original body_html)
  const getTranslatedDescription = (): string | undefined => {
    const p = isEditing ? editedProduct : product;
    if (selectedLanguage === 'de' || !p?.translations?.[selectedLanguage]?.body_html) {
      return p?.body_html;
    }
    return p.translations[selectedLanguage].body_html || p.body_html;
  };

  const getTags = (): string[] => {
    const p = isEditing ? editedProduct : product;
    if (!p?.tags) return [];
    if (Array.isArray(p.tags)) return p.tags;
    return p.tags.split(',').map(tag => tag.trim()).filter(Boolean);
  };

  const getShopifyAdminUrl = (): string => {
    // Default Shopify store domain - in production this would come from config
    const shopDomain = 'imperfekton.myshopify.com';
    return `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com', '')}/products/${productId}`;
  };

  // Edit handlers
  const handleEditField = useCallback((field: string, value: string | number | boolean) => {
    if (!editedProduct) return;
    
    setHasUnsavedChanges(true);
    setEditedProduct(prev => {
      if (!prev) return prev;
      
      // Handle nested fields
      if (field.startsWith('metafields.')) {
        const parts = field.split('.');
        const newMetafields = { ...prev.metafields };
        
        if (parts[1] === 'dimensions') {
          const currentDimensions = newMetafields.dimensions || { height_cm: null, width_cm: null, depth_cm: null };
          newMetafields.dimensions = {
            height_cm: currentDimensions.height_cm ?? null,
            width_cm: currentDimensions.width_cm ?? null,
            depth_cm: currentDimensions.depth_cm ?? null,
            [parts[2]]: value === '' ? null : Number(value)
          };
        } else if (parts[1] === 'weight_g') {
          newMetafields.weight_g = value === '' ? null : Number(value);
        } else if (parts[1] === 'materials' || parts[1] === 'colors') {
          const strValue = String(value);
          (newMetafields as Record<string, unknown>)[parts[1]] = strValue ? strValue.split(',').map(s => s.trim()) : [];
        } else {
          (newMetafields as Record<string, unknown>)[parts[1]] = value;
        }
        
        return { ...prev, metafields: newMetafields };
      }
      
      if (field.startsWith('variants.0.')) {
        const variantField = field.replace('variants.0.', '');
        const newVariants = [...prev.variants];
        if (newVariants[0]) {
          newVariants[0] = { ...newVariants[0], [variantField]: String(value) };
        }
        return { ...prev, variants: newVariants };
      }
      
      if (field === 'tags') {
        // Support both string (legacy) and array (KeywordSelector) input
        if (Array.isArray(value)) {
          return { ...prev, tags: value };
        }
        return { ...prev, tags: String(value) };
      }
      
      return { ...prev, [field]: value };
    });
  }, [editedProduct]);

  const handleImageDelete = (index: number) => {
    if (!editedProduct) return;
    const newImages = [...(editedProduct.images || [])];
    newImages.splice(index, 1);
    setEditedProduct({ ...editedProduct, images: newImages });
    setHasUnsavedChanges(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedProduct || !e.target.files) return;
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setEditedProduct(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            images: [...(prev.images || []), { id: '', src: base64, attachment: base64.split(',')[1], alt: file.name }]
          };
        });
        setHasUnsavedChanges(true);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    if (!editedProduct || !productId) return;
    
    setIsSaving(true);
    try {
      // Prepare update data
      const updateData: Record<string, unknown> = {
        title: editedProduct.title,
        body_html: editedProduct.body_html,
        vendor: editedProduct.vendor,
        product_type: editedProduct.product_type,
        tags: Array.isArray(editedProduct.tags) ? editedProduct.tags : editedProduct.tags,
        status: editedProduct.status,
        images: editedProduct.images,
      };
      
      // Add variant data if changed
      if (editedProduct.variants?.[0]) {
        const variant = editedProduct.variants[0];
        if (variant.price) {
          updateData.price = variant.price;
        }
        if (variant.compare_at_price !== undefined) {
          updateData.compare_at_price = variant.compare_at_price || null;
        }
        if (variant.inventory_quantity !== undefined) {
          updateData.inventory_quantity = variant.inventory_quantity;
        }
        if (variant.sku !== undefined) {
          updateData.sku = variant.sku;
        }
        if (variant.barcode !== undefined) {
          updateData.barcode = variant.barcode;
        }
      }
      
      // Add metafields
      if (editedProduct.metafields) {
        updateData.metafields = {
          dimensions: editedProduct.metafields.dimensions,
          weight_g: editedProduct.metafields.weight_g,
          materials: editedProduct.metafields.materials,
          colors: editedProduct.metafields.colors,
          technique: editedProduct.metafields.technique,
          is_unique: editedProduct.metafields.is_unique,
          artist_name: editedProduct.metafields.artist_name,
        };
      }
      
      const result = await shopifyService.updateProduct(productId, updateData);
      
      if (result.success) {
        toast.success('Produkt erfolgreich aktualisiert');
        setProduct(editedProduct);
        setEditingSection(null);
        setHasUnsavedChanges(false);
      } else {
        toast.error(result.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      console.error('Failed to save product:', err);
      toast.error('Fehler beim Speichern des Produkts');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('Ungespeicherte Änderungen verwerfen?')) {
        return;
      }
    }
    setEditedProduct(product ? JSON.parse(JSON.stringify(product)) : null);
    setEditingSection(null);
    setHasUnsavedChanges(false);
  };
  
  const handleStartSectionEdit = (section: EditableSection) => {
    // Reset edited product to current state before editing
    setEditedProduct(product ? JSON.parse(JSON.stringify(product)) : null);
    setEditingSection(section);
    setHasUnsavedChanges(false);
  };

  const handleRefine = () => {
    if (product && onRefineProduct) {
      // Convert ShopifyProduct to RefinedProductData format
      const tags = getTags();
      const primaryVariant = product.variants?.[0];
      const price = primaryVariant ? parseFloat(primaryVariant.price) : 0;
      const weight = product.metafields?.weight_g || primaryVariant?.weight || 0;
      
      // Create a basic metadata structure from the Shopify product
      const baseMetadata: ProductMetadata = {
        name: product.title,
        description: product.body_html?.replace(/<[^>]*>/g, '') || '', // Strip HTML
        price: price,
        currency: 'EUR',
        category: product.product_type || '',
        materials: product.metafields?.materials || [],
        dimensions: {
          height: product.metafields?.dimensions?.height_cm || 0,
          width: product.metafields?.dimensions?.width_cm || 0,
          depth: product.metafields?.dimensions?.depth_cm || 0,
          weight: weight
        },
        tags: tags
      };

      const refinedData: RefinedProductData = {
        refinedMetadata: {
          de: { ...baseMetadata },
          en: { ...baseMetadata },
          fr: { ...baseMetadata },
          es: { ...baseMetadata }
        },
        sharedInformation: {},
        originalData: { shopify: product },
        correlationId: `refine-${product.id}-${Date.now()}`,
        agentSummary: '',
        shopify_product_id: product.id,
        images: product.images?.map(img => img.src) || []
      };

      onRefineProduct(refinedData);
      navigate('/upload');
    }
  };

  const handleGenerateTranslations = async () => {
    if (!product || !productId) return;
    
    setIsGeneratingTranslations(true);
    try {
      const result = await shopifyService.generateTranslations(productId);
      
      if (result.success) {
        const savedCount = result.results?.translations_saved?.length || 0;
        const updatedCount = result.results?.translations_updated?.length || 0;
        const generatedCount = result.results?.translations_generated?.length || 0;

        let successMessage = '';
        if (savedCount > 0) {
          if (updatedCount > 0 && updatedCount === savedCount) {
            successMessage = `${updatedCount} Übersetzung(en) aktualisiert`;
          } else {
            successMessage = `${savedCount} Übersetzung(en) erfolgreich generiert und gespeichert`;
          }
        } else if (generatedCount > 0) {
          successMessage = `${generatedCount} Übersetzung(en) generiert (keine Änderungen gespeichert)`;
        } else {
          successMessage = result.message || 'Übersetzungen aktualisiert';
        }

        toast.success(successMessage);
        
        // Reload the product to show the new translations
        const updatedProduct = await shopifyService.getProductWithTranslations(productId);
        if (updatedProduct) {
          setProduct(updatedProduct);
          setEditedProduct(JSON.parse(JSON.stringify(updatedProduct)));
        }
      } else {
        toast.error(result.error || 'Fehler beim Generieren der Übersetzungen');
      }
    } catch (err) {
      console.error('Failed to generate translations:', err);
      toast.error('Übersetzungen konnten nicht generiert werden');
    } finally {
      setIsGeneratingTranslations(false);
    }
  };

  // Regenerate German content with AI based on current product data
  const handleRegenerateGerman = async () => {
    if (!product || !productId) return;
    
    setIsRegeneratingGerman(true);
    try {
      // Import the api utility for the refine endpoint
      const { api } = await import('../utils/api');
      
      // Call the refine endpoint with the current German content for AI regeneration
      const response = await api.post('/refine', {
        currentMetadata: {
          de: {
            name: product.title,
            description: product.body_html || '',
            // Include other product data for context
            price: product.variants?.[0]?.price || 0,
            tags: Array.isArray(product.tags) ? product.tags : (product.tags?.split(',').map(t => t.trim()) || []),
            category: product.product_type || '',
            materials: product.metafields?.materials || [],
          }
        },
        instructions: 'Verbessere und überarbeite den deutschen Titel und die Beschreibung. Behalte den Stil bei, aber mache den Text lebendiger und ansprechender für Kunden. Der Text sollte die handwerkliche Qualität und Einzigartigkeit des Produkts hervorheben.',
        targetLanguage: 'de'
      }, {
        showToast: false
      });

      // Type the response data
      const responseData = response.data as { refinedMetadata?: { de?: { name?: string; description?: string } }; error?: string } | undefined;

      // Check for error in response
      if (response.error || responseData?.error) {
        const errorMessage = response.error || responseData?.error || 'Unbekannter Fehler';
        toast.error(`KI-Überarbeitung fehlgeschlagen: ${errorMessage}`);
        return;
      }

      if (responseData?.refinedMetadata?.de) {
        const refined = responseData.refinedMetadata.de;
        
        // Update the product in Shopify
        const updateResult = await shopifyService.updateProduct(productId, {
          title: refined.name || product.title,
          body_html: refined.description || product.body_html
        });

        if (updateResult.success) {
          toast.success('Deutscher Text wurde mit KI überarbeitet');
          
          // Reload the product to show the updated content
          const updatedProduct = await shopifyService.getProductWithTranslations(productId);
          if (updatedProduct) {
            setProduct(updatedProduct);
            setEditedProduct(JSON.parse(JSON.stringify(updatedProduct)));
          }
        } else {
          toast.error(updateResult.error || 'Fehler beim Speichern des überarbeiteten Textes');
        }
      } else {
        toast.error('KI-Überarbeitung hat keine Ergebnisse zurückgegeben');
      }
    } catch (err) {
      console.error('Failed to regenerate German content:', err);
      toast.error('Deutscher Text konnte nicht überarbeitet werden');
    } finally {
      setIsRegeneratingGerman(false);
    }
  };

  // Start editing a translation for the selected language (not German, which uses main fields)
  const handleStartEditTranslation = () => {
    if (!product || selectedLanguage === 'de') return;
    
    const currentTranslation = product.translations?.[selectedLanguage];
    setEditedTranslation({
      title: currentTranslation?.title || product.title,
      body_html: currentTranslation?.body_html || product.body_html || ''
    });
    setIsEditingTranslation(true);
  };

  // Cancel translation editing
  const handleCancelTranslationEdit = () => {
    setIsEditingTranslation(false);
    setEditedTranslation({ title: '', body_html: '' });
  };

  // Save translation for the selected language
  const handleSaveTranslation = async () => {
    if (!productId || selectedLanguage === 'de') return;
    
    setIsSavingTranslation(true);
    try {
      const result = await shopifyService.updateTranslation(productId, selectedLanguage, {
        title: editedTranslation.title,
        body_html: editedTranslation.body_html
      });
      
      if (result.success) {
        toast.success(`${LANGUAGE_NAMES[selectedLanguage] || selectedLanguage} Übersetzung gespeichert`);
        
        // Update local state with new translation
        setProduct(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            translations: {
              ...prev.translations,
              [selectedLanguage]: {
                title: editedTranslation.title,
                body_html: editedTranslation.body_html
              }
            }
          };
        });
        setEditedProduct(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            translations: {
              ...prev.translations,
              [selectedLanguage]: {
                title: editedTranslation.title,
                body_html: editedTranslation.body_html
              }
            }
          };
        });
        
        setIsEditingTranslation(false);
      } else {
        toast.error(result.error || 'Fehler beim Speichern der Übersetzung');
      }
    } catch (err) {
      console.error('Failed to save translation:', err);
      toast.error('Übersetzung konnte nicht gespeichert werden');
    } finally {
      setIsSavingTranslation(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warmGray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clay-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Produkt wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-warmGray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error || 'Produkt nicht gefunden'}</p>
          <Link
            to="/products"
            className="mt-4 inline-flex items-center text-clay-600 hover:text-clay-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Zurück zur Produktliste
          </Link>
        </div>
      </div>
    );
  }

  const primaryVariant = (isEditing ? editedProduct : product)?.variants?.[0];
  const tags = getTags();
  const displayProduct = isEditing ? editedProduct : product;

  return (
    <div className="min-h-screen bg-warmGray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - mobile responsive */}
        <div className="mb-6">
          {/* Title row */}
          <div className="flex items-start gap-2 mb-3">
            <Link
              to="/products"
              className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Zurück zur Produktliste"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-pottery-earth break-words">{product.title}</h1>
              <p className="text-xs sm:text-sm text-gray-500">ID: {product.id}</p>
            </div>
          </div>
          {/* Action buttons - wrap on mobile */}
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {onRefineProduct && (
              <button
                onClick={handleRefine}
                disabled={isEditing}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                AI Verfeinern
              </button>
            )}
            <a
              href={getShopifyAdminUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1 sm:mr-2" />
              Shopify
            </a>
          </div>
        </div>

        {/* Unsaved changes warning */}
        {hasUnsavedChanges && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Sie haben ungespeicherte Änderungen. Klicken Sie auf &quot;Speichern&quot;, um die Änderungen zu übernehmen.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Images */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <SectionHeader
                  title="Bilder"
                  section="images"
                  editingSection={editingSection}
                  onEdit={handleStartSectionEdit}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
                {displayProduct?.images && displayProduct.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {displayProduct.images.map((image, index) => (
                      <div key={image.id || index} className="relative aspect-square group">
                        <img
                          src={image.src}
                          alt={image.alt || `Bild ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTI4IDI4TDM2IDM2TDQ0IDI4IiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';
                          }}
                        />
                        {editingSection === 'images' && (
                          <button
                            onClick={() => handleImageDelete(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Bild löschen"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {editingSection === 'images' && (
                      <div className="relative aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors">
                        <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                          <PlusIcon className="h-8 w-8 text-gray-400" />
                          <span className="text-xs text-gray-500 mt-1">Bild hinzufügen</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-500 text-sm mb-2">Keine Bilder verfügbar</p>
                    {editingSection === 'images' && (
                      <label className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Bilder hochladen
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status & Basic Info */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <SectionHeader
                  title="Produktinformationen"
                  section="info"
                  editingSection={editingSection}
                  onEdit={handleStartSectionEdit}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      {editingSection === 'info' ? (
                        <select
                          value={editedProduct?.status || 'draft'}
                          onChange={(e) => handleEditField('status', e.target.value)}
                          className="border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2"
                        >
                          <option value="active">Aktiv</option>
                          <option value="draft">Entwurf</option>
                          <option value="archived">Archiviert</option>
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          displayProduct?.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : displayProduct?.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {displayProduct?.status === 'active' ? 'Aktiv' : displayProduct?.status === 'draft' ? 'Entwurf' : displayProduct?.status}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Produkttyp</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {editingSection === 'info' ? (
                        <CategoryCombobox
                          value={displayProduct?.product_type || ''}
                          onChange={(v) => handleEditField('product_type', v)}
                          placeholder="Kategorie wählen"
                          className="w-full"
                        />
                      ) : (
                        displayProduct?.product_type || '-'
                      )}
                    </dd>
                  </div>
                  <EditableField
                    label="Anbieter"
                    value={displayProduct?.vendor}
                    isEditing={editingSection === 'info'}
                    onChange={(v) => handleEditField('vendor', v)}
                  />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Handle</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">{displayProduct?.handle}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Erstellt</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(product.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Aktualisiert</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(product.updated_at)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Dimensions & Weight Section - NEW for #110 */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <SectionHeader
                  title="Abmessungen & Gewicht"
                  section="dimensions"
                  editingSection={editingSection}
                  onEdit={handleStartSectionEdit}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
                <dl className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4">
                  <EditableField
                    label="Höhe"
                    value={displayProduct?.metafields?.dimensions?.height_cm}
                    isEditing={editingSection === 'dimensions'}
                    onChange={(v) => handleEditField('metafields.dimensions.height_cm', v)}
                    type="number"
                    suffix="cm"
                  />
                  <EditableField
                    label="Breite"
                    value={displayProduct?.metafields?.dimensions?.width_cm}
                    isEditing={editingSection === 'dimensions'}
                    onChange={(v) => handleEditField('metafields.dimensions.width_cm', v)}
                    type="number"
                    suffix="cm"
                  />
                  <EditableField
                    label="Tiefe"
                    value={displayProduct?.metafields?.dimensions?.depth_cm}
                    isEditing={editingSection === 'dimensions'}
                    onChange={(v) => handleEditField('metafields.dimensions.depth_cm', v)}
                    type="number"
                    suffix="cm"
                  />
                  <EditableField
                    label="Gewicht"
                    value={displayProduct?.metafields?.weight_g}
                    isEditing={editingSection === 'dimensions'}
                    onChange={(v) => handleEditField('metafields.weight_g', v)}
                    type="number"
                    suffix="g"
                  />
                </dl>
              </div>
            </div>

            {/* Pottery-Specific Properties */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <SectionHeader
                  title="Keramik-Eigenschaften"
                  section="ceramics"
                  editingSection={editingSection}
                  onEdit={handleStartSectionEdit}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <EditableField
                    label="Materialien"
                    value={displayProduct?.metafields?.materials?.join(', ')}
                    isEditing={editingSection === 'ceramics'}
                    onChange={(v) => handleEditField('metafields.materials', v)}
                  />
                  <EditableField
                    label="Farben"
                    value={displayProduct?.metafields?.colors?.join(', ')}
                    isEditing={editingSection === 'ceramics'}
                    onChange={(v) => handleEditField('metafields.colors', v)}
                  />
                  <EditableField
                    label="Technik"
                    value={displayProduct?.metafields?.technique}
                    isEditing={editingSection === 'ceramics'}
                    onChange={(v) => handleEditField('metafields.technique', v)}
                  />
                  <EditableField
                    label="Künstler"
                    value={displayProduct?.metafields?.artist_name}
                    isEditing={editingSection === 'ceramics'}
                    onChange={(v) => handleEditField('metafields.artist_name', v)}
                  />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Unikat</dt>
                    <dd className="mt-1">
                      {editingSection === 'ceramics' ? (
                        <input
                          type="checkbox"
                          checked={displayProduct?.metafields?.is_unique ?? false}
                          onChange={(e) => handleEditField('metafields.is_unique', e.target.checked)}
                          className="h-4 w-4 text-clay-600 focus:ring-clay-500 border-gray-300 rounded"
                        />
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          displayProduct?.metafields?.is_unique
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {displayProduct?.metafields?.is_unique ? 'Ja' : 'Nein'}
                        </span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Description with Language Tabs */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                {selectedLanguage === 'de' ? (
                  <SectionHeader
                    title="Beschreibung & Übersetzungen"
                    section="description"
                    editingSection={editingSection}
                    onEdit={handleStartSectionEdit}
                    onSave={handleSave}
                    onCancel={handleCancelEdit}
                    isSaving={isSaving}
                  />
                ) : (
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      <LanguageIcon className="h-5 w-5 text-gray-500" />
                      Beschreibung & Übersetzungen
                    </h3>
                  </div>
                )}
                
                {/* Language Tabs */}
                {getAvailableLanguages().length > 1 && (
                  <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                      {getAvailableLanguages().map((lang) => (
                        <button
                          key={lang}
                          onClick={() => setSelectedLanguage(lang)}
                          className={`
                            whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm transition-colors
                            ${selectedLanguage === lang
                              ? 'border-clay-500 text-clay-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                          `}
                        >
                          {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Title and Description in selected language */}
                <div className="space-y-4">
                  {editingSection === 'description' && selectedLanguage === 'de' ? (
                    <>
                      <EditableField
                        label="Titel"
                        value={editedProduct?.title}
                        isEditing={true}
                        onChange={(v) => handleEditField('title', v)}
                      />
                      <EditableField
                        label="Beschreibung"
                        value={editedProduct?.body_html}
                        isEditing={true}
                        onChange={(v) => handleEditField('body_html', v)}
                        type="textarea"
                      />
                    </>
                  ) : isEditingTranslation && selectedLanguage !== 'de' ? (
                    /* Translation editing mode */
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-600">
                          Bearbeite {LANGUAGE_NAMES[selectedLanguage] || selectedLanguage} Übersetzung
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelTranslationEdit}
                            disabled={isSavingTranslation}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Abbrechen
                          </button>
                          <button
                            onClick={handleSaveTranslation}
                            disabled={isSavingTranslation}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          >
                            {isSavingTranslation ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                Speichern...
                              </>
                            ) : (
                              <>
                                <CheckIcon className="h-4 w-4 mr-1" />
                                Speichern
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Titel</label>
                        <input
                          type="text"
                          value={editedTranslation.title}
                          onChange={(e) => setEditedTranslation(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Beschreibung</label>
                        <textarea
                          value={editedTranslation.body_html}
                          onChange={(e) => setEditedTranslation(prev => ({ ...prev, body_html: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                          rows={6}
                        />
                      </div>
                    </>
                  ) : (
                    /* Read-only display mode */
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <dt className="text-sm font-medium text-gray-500 mb-1">Titel</dt>
                          <dd className="text-lg font-semibold text-gray-900">{getTranslatedTitle()}</dd>
                        </div>
                        {selectedLanguage !== 'de' && !isEditing && (
                          <button
                            onClick={handleStartEditTranslation}
                            className="ml-4 inline-flex items-center px-3 py-1 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Bearbeiten
                          </button>
                        )}
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 mb-1">Beschreibung</dt>
                        <dd>
                          {getTranslatedDescription() ? (
                            <div 
                              className="prose prose-sm max-w-none text-gray-600"
                              dangerouslySetInnerHTML={{ __html: getTranslatedDescription() || '' }}
                            />
                          ) : (
                            <p className="text-gray-500 text-sm">Keine Beschreibung verfügbar</p>
                          )}
                        </dd>
                      </div>
                      
                      {/* AI Regenerate button for German content (outside edit mode) */}
                      {selectedLanguage === 'de' && !isEditing && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                              </svg>
                              <p className="text-sm text-amber-800">
                                Titel und Beschreibung mit KI überarbeiten und verbessern
                              </p>
                            </div>
                            <button
                              onClick={handleRegenerateGerman}
                              disabled={isRegeneratingGerman}
                              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isRegeneratingGerman ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Überarbeite mit KI...
                                </>
                              ) : (
                                <>
                                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                                  </svg>
                                  Mit KI überarbeiten
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Show all languages overview */}
                {displayProduct?.translations && Object.keys(displayProduct.translations).length > 0 && !isEditing && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Verfügbare Übersetzungen</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.keys(displayProduct.translations).map((locale) => {
                        const translation = displayProduct.translations?.[locale];
                        const hasTitle = !!translation?.title;
                        const hasDescription = !!translation?.body_html;
                        return (
                          <div
                            key={locale}
                            className={`p-3 rounded-lg border ${
                              selectedLanguage === locale
                                ? 'border-clay-500 bg-clay-50'
                                : 'border-gray-200 hover:border-gray-300'
                            } cursor-pointer transition-colors`}
                            onClick={() => setSelectedLanguage(locale)}
                          >
                            <div className="font-medium text-sm text-gray-900">
                              {LANGUAGE_NAMES[locale] || locale.toUpperCase()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {hasTitle && hasDescription && '✓ Titel & Beschreibung'}
                              {hasTitle && !hasDescription && '✓ Nur Titel'}
                              {!hasTitle && hasDescription && '✓ Nur Beschreibung'}
                              {!hasTitle && !hasDescription && '○ Keine Daten'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Translation actions - always show regenerate option */}
                {!isEditing && !isEditingTranslation && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <LanguageIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                        <p className="text-sm text-blue-800">
                          {(!displayProduct?.translations || Object.keys(displayProduct.translations).length === 0)
                            ? 'Keine Übersetzungen vorhanden. Generieren Sie Übersetzungen aus dem deutschen Original.'
                            : 'Übersetzungen aus dem deutschen Original (neu) generieren?'
                          }
                        </p>
                      </div>
                      <button
                        onClick={handleGenerateTranslations}
                        disabled={isGeneratingTranslations}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingTranslations ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Generiere Übersetzungen...
                          </>
                        ) : (
                          <>
                            <LanguageIcon className="h-4 w-4 mr-2" />
                            {(!displayProduct?.translations || Object.keys(displayProduct.translations).length === 0)
                              ? 'Übersetzungen generieren'
                              : 'Neu generieren'
                            }
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing & Inventory */}
            {primaryVariant && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 py-5 sm:p-6">
                  <SectionHeader
                    title="Preis & Bestand"
                    section="pricing"
                    editingSection={editingSection}
                    onEdit={handleStartSectionEdit}
                    onSave={handleSave}
                    onCancel={handleCancelEdit}
                    isSaving={isSaving}
                  />
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Preis</dt>
                      <dd className="mt-1">
                        {editingSection === 'pricing' ? (
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              value={editedProduct?.variants?.[0]?.price || ''}
                              onChange={(e) => handleEditField('variants.0.price', e.target.value)}
                              className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2 pr-8"
                            />
                            <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">€</span>
                          </div>
                        ) : (
                          <span className="text-2xl font-bold text-pottery-earth">
                            {formatPrice(primaryVariant.price)}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Streichpreis</dt>
                      <dd className="mt-1">
                        {editingSection === 'pricing' ? (
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              value={editedProduct?.variants?.[0]?.compare_at_price || ''}
                              onChange={(e) => handleEditField('variants.0.compare_at_price', e.target.value)}
                              className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2 pr-8"
                              placeholder="Für Rabatte"
                            />
                            <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">€</span>
                          </div>
                        ) : primaryVariant.compare_at_price ? (
                          <span className="text-lg text-gray-500 line-through">
                            {formatPrice(primaryVariant.compare_at_price)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Bestand</dt>
                      <dd className="mt-1">
                        {editingSection === 'pricing' ? (
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={editedProduct?.variants?.[0]?.inventory_quantity ?? 0}
                            onChange={(e) => handleEditField('variants.0.inventory_quantity', parseInt(e.target.value) || 0)}
                            className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-clay-500 focus:border-clay-500 sm:text-sm p-2"
                          />
                        ) : (
                          <>
                            <span className={`text-2xl font-bold ${
                              primaryVariant.inventory_quantity <= 0 
                                ? 'text-red-600' 
                                : primaryVariant.inventory_quantity <= 3 
                                ? 'text-yellow-600' 
                                : 'text-green-600'
                            }`}>
                              {primaryVariant.inventory_quantity ?? '-'}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">Stück</span>
                          </>
                        )}
                      </dd>
                    </div>
                    <EditableField
                      label="SKU"
                      value={primaryVariant.sku}
                      isEditing={editingSection === 'pricing'}
                      onChange={(v) => handleEditField('variants.0.sku', v)}
                    />
                    <EditableField
                      label="Barcode"
                      value={primaryVariant.barcode}
                      isEditing={editingSection === 'pricing'}
                      onChange={(v) => handleEditField('variants.0.barcode', v)}
                    />
                  </dl>
                </div>
              </div>
            )}

            {/* Variants (if multiple) */}
            {displayProduct?.variants && displayProduct.variants.length > 1 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Varianten ({displayProduct.variants.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Titel</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Preis</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bestand</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {displayProduct.variants.map((variant) => (
                          <tr key={variant.id}>
                            <td className="px-3 py-2 text-sm text-gray-900">{variant.title}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{formatPrice(variant.price)}</td>
                            <td className="px-3 py-2 text-sm text-gray-500 font-mono">{variant.sku || '-'}</td>
                            <td className="px-3 py-2 text-sm">
                              <span className={
                                variant.inventory_quantity <= 0 
                                  ? 'text-red-600' 
                                  : variant.inventory_quantity <= 3 
                                  ? 'text-yellow-600' 
                                  : 'text-green-600'
                              }>
                                {variant.inventory_quantity ?? '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <SectionHeader
                  title="Tags / Keywords"
                  section="tags"
                  editingSection={editingSection}
                  onEdit={handleStartSectionEdit}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
                {editingSection === 'tags' ? (
                  <KeywordSelector
                    value={tags}
                    onChange={(keywords) => handleEditField('tags', keywords as unknown as string)}
                    existingKeywords={existingKeywords}
                    placeholder="Neues Keyword hinzufügen..."
                  />
                ) : tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-clay-100 text-clay-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine Tags vorhanden</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
